import PropTypes from "prop-types";
import sizeMe from "react-sizeme";
import React, { useRef, useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { CardHeader, CardContent, Box } from "@mui/material";
import { DropdownSelect } from "../../components/DropdownSelect";
import DashboardWidgetMenu from "./DashboardWidgetMenu";
import FadedContainer from "../../components/FadedContainer/FadedContainer";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  cardHeader: {
    width: "100%",
    zIndex: 10,
    flexWrap: "wrap",
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
  },
  cardContent: {
    padding: theme.spacing(0, 4),
    overflowY: "auto",
    justifyContent: "flex-start",
    "&:last-child": {
      paddingBottom: 0
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 2)
    }
  },
  footer: {
    borderTop: `solid 1px ${theme.palette.divider}`,
    padding: theme.spacing(2, 0),
    margin: theme.spacing(0, 4),
    backgroundColor: theme.palette.common.white,
    zIndex: 1
  },
  subheader: {
    padding: theme.spacing(3, 4, 2, 4),
    marginBottom: theme.spacing(-3),
    backgroundColor: theme.palette.common.white,
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 2, 2, 2)
    }
  },
  fadedContainer: {
    padding: 0
  }
}));
const MeasuredContent = sizeMe({ monitorHeight: true })(({ children }) => {
  return (
    <Box pt={4} pb={3}>
      {children}
    </Box>
  );
});
const DashboardWidgetContent = ({
  children,
  title,
  headerAction,
  dropdownProps,
  menuProps,
  defaultMenuOptions,
  defaultMenuItemClick,
  subheader,
  footer,
  collapsed,
  onSize,
  fadeEffect
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const headerRef = useRef(null);
  const subheaderRef = useRef(null);
  const footerRef = useRef(null);
  const { t } = useTranslation();

  const [contentSize, setContentSize] = useState(0);
  const [label, setLabel] = useState(t("dashboardWidget.options.collapse"));

  defaultMenuOptions[0].label = label;

  const handleSizeChange = ({ height }) => {
    const subheaderHeight = !!subheaderRef?.current
      ? subheaderRef.current.offsetHeight - 24
      : 0;
    if (!collapsed) {
      const newSize =
        height +
        headerRef.current.offsetHeight +
        subheaderHeight +
        (footerRef?.current?.offsetHeight || 0);
      onSize(newSize);
      setContentSize(newSize);
    }
  };
  const measuredContent = (
    <MeasuredContent onSize={handleSizeChange}>{children}</MeasuredContent>
  );

  useEffect(() => {
    let label;
    if (collapsed) {
      onSize(headerRef.current.offsetHeight);
      label = "expand";
    } else {
      label = "collapse";
    }
    setTimeout(() => {
      setLabel(t(`dashboardWidget.options.${label}`));
    }, 400);
  }, [collapsed]);

  return (
    <>
      <CardHeader
        className={classes.cardHeader}
        title={title}
        titleTypographyProps={{ variant: "h6" }}
        ref={headerRef}
        action={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            pl={2}
            color={theme.palette.common.white}
          >
            {headerAction}
            {dropdownProps && <DropdownSelect {...dropdownProps} />}
            {menuProps && (
              <DashboardWidgetMenu
                {...menuProps}
                defaultMenuOptions={defaultMenuOptions}
                defaultMenuItemClick={defaultMenuItemClick}
              />
            )}
          </Box>
        }
      />
      {!collapsed && subheader && (
        <Box className={classes.subheader} ref={subheaderRef}>
          {subheader}
        </Box>
      )}
      {!collapsed && (
        <CardContent className={classes.cardContent}>
          {!collapsed &&
            (fadeEffect ? (
              <FadedContainer fadeHeight={120} containerSize={contentSize}>
                {measuredContent}
              </FadedContainer>
            ) : (
              measuredContent
            ))}
        </CardContent>
      )}
      {!collapsed && footer && (
        <Box className={classes.footer} ref={footerRef}>
          {footer}
        </Box>
      )}
    </>
  );
};

export default DashboardWidgetContent;

DashboardWidgetContent.propTypes = {
  title: PropTypes.string,
  children: PropTypes.any,
  headerAction: PropTypes.any,
  dropdownProps: PropTypes.object,
  menuProps: PropTypes.object,
  defaultMenuOptions: PropTypes.array,
  defaultMenuItemClick: PropTypes.func,
  footer: PropTypes.node,
  collapsed: PropTypes.bool,
  fadeEffect: PropTypes.bool
};
